import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell, Check, CheckCheck, ChevronRight, Inbox,
  FileCheck, CheckCircle2, XCircle, Undo2, Megaphone, HandCoins,
  ArrowLeftRight, Store, Mail, BadgeCheck, Lock, UserCog,
  Sparkles, MessageSquare, Eye,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";

// Map server-provided icon string → lucide component (keeps backend simple)
const ICON_MAP = {
  Bell, FileCheck, CheckCircle2, XCircle, Undo2, Megaphone, HandCoins,
  ArrowLeftRight, Store, Mail, BadgeCheck, Lock, UserCog,
  Sparkles, MessageSquare, Eye,
};

function fmtAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications?limit=10");
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch { /* ignore — bell is best-effort */ }
    finally { setLoading(false); }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  // Initial + poll-on-focus + light interval poll (no websockets per MVP scope).
  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    fetchCount();
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    const id = setInterval(fetchCount, 60_000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(id); };
  }, [user, fetchCount]);

  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  if (!user) return null;

  const markRead = async (notifId) => {
    setItems((arr) => arr.map((n) => n.notif_id === notifId ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${notifId}/read`); }
    catch { fetchAll(); }
  };

  const markAllRead = async () => {
    try {
      const r = await api.post("/notifications/read-all");
      toast.success(`Marked ${r.data.updated || 0} read`);
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const onClickItem = async (n) => {
    if (!n.read) await markRead(n.notif_id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifications"
          className="relative w-9 h-9 border border-border flex items-center justify-center hover:bg-muted">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        data-testid="notification-dropdown"
        className="rounded-none w-80 sm:w-96 p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="font-overline text-xs">Notifications</span>
            {unread > 0 && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-500 text-white">
                {unread} new
              </span>
            )}
          </div>
          {unread > 0 && (
            <button data-testid="notification-mark-all"
              onClick={markAllRead}
              className="text-xs text-primary hover:underline flex items-center gap-1">
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="notification-list">
          {loading && items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="notif-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div data-testid="notif-empty" className="p-8 text-center">
              <Inbox className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
              <div className="text-sm text-muted-foreground">You're all caught up.</div>
              <div className="text-xs text-muted-foreground/70 mt-1">No notifications yet.</div>
            </div>
          ) : (
            items.map((n) => {
              const Icon = ICON_MAP[n.icon] || Bell;
              return (
                <button key={n.notif_id} data-testid={`notif-${n.notif_id}`}
                  onClick={() => onClickItem(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-border/60 hover:bg-muted/50 transition-colors ${!n.read ? "bg-primary/5" : ""}`}>
                  <div className={`mt-0.5 w-7 h-7 flex items-center justify-center flex-shrink-0 ${!n.read ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm truncate ${!n.read ? "font-semibold" : ""}`}>{n.title}</span>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" data-testid="notif-unread-dot" />}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.description}</div>
                    <div className="text-[10px] text-muted-foreground/70 mt-1 uppercase tracking-wider">{fmtAgo(n.created_at)} · {n.category}</div>
                  </div>
                  {!n.read && (
                    <span data-testid={`notif-mark-${n.notif_id}`}
                      onClick={(e) => { e.stopPropagation(); markRead(n.notif_id); }}
                      role="button" aria-label="Mark read"
                      className="opacity-60 hover:opacity-100 hover:bg-background p-1">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <button data-testid="notification-view-all"
          onClick={() => { setOpen(false); navigate("/notifications"); }}
          className="w-full px-4 py-2.5 border-t border-border text-sm hover:bg-muted flex items-center justify-center gap-1.5 font-medium">
          View all notifications <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </PopoverContent>
    </Popover>
  );
}
